type TimestampInput = string | null | undefined;

const isNumber = (value: number | null): value is number => value !== null;

export const toAttackPathTimestamp = (value: TimestampInput): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();

  return Number.isFinite(timestamp) ? timestamp : null;
};

export const resolveAttackPathStartMs = ({
  startedAt,
  occurredAtValues,
}: {
  startedAt: TimestampInput;
  occurredAtValues: readonly TimestampInput[];
}): number | null => {
  const candidates = [
    toAttackPathTimestamp(startedAt),
    ...occurredAtValues.map(toAttackPathTimestamp),
  ].filter(isNumber);

  return candidates.length === 0 ? null : Math.min(...candidates);
};

export const attackPathElapsedSeconds = ({
  occurredAt,
  startMs,
}: {
  occurredAt: TimestampInput;
  startMs: number | null;
}): number => {
  const occurredMs = toAttackPathTimestamp(occurredAt);

  if (occurredMs === null || startMs === null) {
    return 0;
  }

  return Math.round((occurredMs - startMs) / 1000);
};

export const resolveAttackPathChainDurationSeconds = (chain: {
  startedAt: TimestampInput;
  lastActivityAt: TimestampInput;
  completedAt: TimestampInput;
  events: ReadonlyArray<{ occurredAt: TimestampInput }>;
}): number | null => {
  const occurredAtValues = chain.events.map((event) => event.occurredAt);
  const startMs = resolveAttackPathStartMs({
    startedAt: chain.startedAt,
    occurredAtValues,
  });
  const endCandidates = [
    toAttackPathTimestamp(chain.lastActivityAt),
    toAttackPathTimestamp(chain.completedAt),
    ...occurredAtValues.map(toAttackPathTimestamp),
  ].filter(isNumber);

  if (startMs === null || endCandidates.length === 0) {
    return null;
  }

  const durationMs = Math.max(...endCandidates) - startMs;

  return durationMs > 0 ? Math.round(durationMs / 1000) : null;
};
